import {isMobile} from 'react-device-detect';
import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {AudioItem, AudioItemObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import {createAudioItemFromObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import type {AudioData} from '@Libraries/add-media-library';
import {getUniqueString} from '@Utils/string.util';
import {getAudioUrl} from '@Libraries/user-audio-library';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import type {TimelineResizeData} from '@PosterWhiteboard/items/item/item.types';
import {MIN_ITEM_DURATION} from '@PosterWhiteboard/items/item/item.types';
import type {DeepPartial} from '@/global';
import {recalculateTimelineSubRows} from '@Libraries/timeline-sub-rows.library';
import {commitPosterMutation} from '@PosterWhiteboard/poster/commit-poster-mutation';
import {getGroupResizedSpan} from '@PosterWhiteboard/page/timeline-group-gestures.library';
import type {TimelineResizeHandle} from '@PosterWhiteboard/page/timeline-group-gestures.library';

const ADDING_AUDIO_LOADING_KEY = 'addingAudioItemsData';

export interface AudioClipsObject {
  audioItemsHashMap: Record<string, AudioItemObject>;
}

export interface AddAudioData extends AudioData {
  uid?: string;
  onPageStartTime?: number;
  /** Overrides the S3 URL normally derived from filename+src — lets tests drive the real add flow against a served fixture. */
  audioUrl?: string;
}

interface TrimAudioItemOpts {
  undoable?: boolean;
}

interface AddAudioItemsOpts {
  undoable?: boolean;
  updateRedux?: boolean;
  selectOnAdd?: boolean;
}

export class AudioClips extends CommonMethods {
  public audioItemsHashMap: Record<string, AudioItem> = {};
  /**
   * The timeline's audio selection — one uid for a single selection, two or more for
   * a multi-selection. UI state, deliberately NOT part of AudioClipsObject/toObject:
   * selection is not document state, so history diffs, undo/redo, and saves never
   * carry it (the backend shape carries no selection either). Single-clip consumers
   * (panels, bottom bar) read through getSelectedAudioItem /
   * getSingleSelectedAudioItemUID, which resolve only when exactly one clip is
   * selected — a multi-selection reads as "nothing selected" to them, mirroring how
   * canvas items behave.
   */
  public selectedAudioItemUIDs: string[] = [];
  public page: Page;

  public constructor(page: Page) {
    super();
    this.page = page;
  }

  public hasAudio(): boolean {
    return Object.keys(this.audioItemsHashMap).length !== 0;
  }

  public selectAudioItem(uid: string): void {
    this.page.poster.clearItemSelection();
    this.#setSelection([uid]);
  }

  /**
   * Toggles an audio bar in/out of the audio multi-selection — the timeline's
   * modifier-click gesture. Unlike selectAudioItem this never clears the canvas
   * selection: modifier-clicks build MIXED item+audio selections that gesture as one.
   */
  public toggleAudioItemInMultiSelection(uid: string): void {
    const currentUIDs = this.getSelectedAudioItemUIDs();
    this.#setSelection(currentUIDs.includes(uid) ? currentUIDs.filter((selectedUid) => selectedUid !== uid) : [...currentUIDs, uid]);
  }

  /**
   * The single write path for the audio selection — every selection change, from any
   * call site, flows through here so the Redux mirror can never go stale against the
   * class state.
   */
  #setSelection(uids: string[]): void {
    this.selectedAudioItemUIDs = uids;
    this.page.poster.redux.updateAudioSelection();
  }

  /**
   * A user-facing audio add should land with its clip visible on the desktop timeline, so
   * open timeline mode if it's closed. Must run AFTER updateReduxData() has published the
   * clip: the editor's `isTimelineModeActive && !isCurrentPageVideo` watchdog closes the
   * timeline when the mirror doesn't yet make the page a video page. Mobile keeps its own
   * timeline-modal + selection-footer flow.
   */
  #revealAddedAudioOnTimeline(): void {
    if (!isMobile) {
      this.page.poster.updateIsTimelineMode(true);
    }
  }

  /**
   * Drops selection uids whose clips no longer exist — undo/redo and load rewrite the
   * clip map wholesale and must not leave ghost members in the selection or the
   * Redux mirror.
   */
  #pruneSelectionToLiveClips(): void {
    const liveUIDs = this.getSelectedAudioItemUIDs();
    if (liveUIDs.length !== this.selectedAudioItemUIDs.length) {
      this.#setSelection(liveUIDs);
    }
  }

  /**
   * Sets the audio side of the timeline selection outright — the shift-range
   * gesture's write path. Never touches the canvas selection: range selections are
   * mixed selections, and the item half is applied by the canvas owner.
   */
  public setSelectedAudioItems(uids: string[]): void {
    this.#setSelection(uids.filter((uid) => this.audioItemsHashMap[uid] !== undefined));
  }

  /**
   * The audio side of the timeline selection, filtered against the live clip map so
   * uids left behind by an undo that removed clips self-heal instead of leaking into
   * gestures.
   */
  public getSelectedAudioItemUIDs(): string[] {
    return this.selectedAudioItemUIDs.filter((uid) => this.audioItemsHashMap[uid] !== undefined);
  }

  public unselectAudioPlaylist(): void {
    if (this.selectedAudioItemUIDs.length > 0) {
      this.#setSelection([]);
    }
  }

  public toObject(): AudioClipsObject {
    const audioItemObjects: Record<string, AudioItemObject> = {};

    for (const [key, audioItem] of Object.entries(this.audioItemsHashMap)) {
      audioItemObjects[key] = audioItem.toObject();
    }

    return {
      audioItemsHashMap: audioItemObjects,
    };
  }

  /** The selected clip when EXACTLY one is selected — multi-selections read as "nothing" to single-clip consumers. */
  public getSelectedAudioItem(): AudioItem | undefined {
    const selectedUIDs = this.getSelectedAudioItemUIDs();
    return selectedUIDs.length === 1 ? this.audioItemsHashMap[selectedUIDs[0]] : undefined;
  }

  public deleteSelectedAudioItem(): void {
    const audioItem = this.getSelectedAudioItem();
    if (audioItem) {
      this.doRemoveItem(audioItem.uid);
      this.recalculateTimelineSubRows();
      const minOnPageStartTimeAudioItem = this.getMinOnPageStartTimeAudioItem();
      this.#setSelection(minOnPageStartTimeAudioItem ? [minOnPageStartTimeAudioItem.uid] : []);
      this.page.poster.fire('modified');
      this.page.poster.redux.updateReduxData();
      this.page.poster.history.addPosterHistory();
      this.updatePosterTimeIfNeeded();
    }
  }

  /**
   * Removes the given clips WITHOUT committing — the caller owns the history entry.
   * Lets the mixed timeline-selection delete land items and audio in ONE undo step.
   */
  public deleteAudioItems(uids: string[]): void {
    for (const uid of uids) {
      if (this.audioItemsHashMap[uid]) {
        this.doRemoveItem(uid);
      }
    }
    this.recalculateTimelineSubRows();
    this.#pruneSelectionToLiveClips();
  }

  public updatePosterTimeIfNeeded(): void {
    if (!this.page.poster.isVideo()) {
      void this.page.poster.stop();
    }
  }

  public async replaceAudioItem(audioItemIdToReplace: string, audioItemToAdd: AudioData): Promise<void> {
    const {onPageStartTime} = this.audioItemsHashMap[audioItemIdToReplace];
    const uid = getUniqueString();
    await this.addAudioItemsData(
      [
        {
          ...audioItemToAdd,
          uid,
          onPageStartTime,
        },
      ],
      {
        updateRedux: false,
        undoable: false,
        selectOnAdd: false,
      }
    );
    this.doRemoveItem(audioItemIdToReplace);
    this.recalculateTimelineSubRows();
    this.page.poster.redux.updateReduxData();
    this.#setSelection([uid]);
    await this.page.poster.seekPosterToSelectedAudioItem();
    this.page.poster.history.addPosterHistory();
    this.page.poster.redux.updateReduxData();
  }

  public async addAudioItemsData(audioItemsData: AddAudioData[], {updateRedux = true, undoable = true, selectOnAdd = true}: AddAudioItemsOpts = {}): Promise<void> {
    showLoading(ADDING_AUDIO_LOADING_KEY, {
      text: audioItemsData.length > 1 ? window.i18next.t('pmwjs_loading_audio_clips') : window.i18next.t('pmwjs_loading_audio_clip'),
    });

    try {
      const addedUIDs = await Promise.all(audioItemsData.map((audioItemData) => this.addAudioItemData(audioItemData)));
      this.recalculateTimelineSubRows();

      if (undoable) {
        this.page.poster.history.addPosterHistory();
      }
      if (updateRedux) {
        this.page.poster.redux.updateReduxData();
      }
      // Select only after the poster mirror is published — a selection dispatch pointing at a uid
      // the mirror doesn't have yet crashes the audio side panel (getSelectedAudioItem joins on it).
      if (selectOnAdd && addedUIDs.length > 0) {
        this.#setSelection([addedUIDs[addedUIDs.length - 1]]);
        this.#revealAddedAudioOnTimeline();
      }
    } finally {
      hideLoading(ADDING_AUDIO_LOADING_KEY);
    }
  }

  private async addAudioItemData(audioItemData: AddAudioData): Promise<string> {
    const onPageStartTime = audioItemData.onPageStartTime ?? this.page.clock.getCurrentTime();
    const uid = audioItemData.uid ?? getUniqueString();

    const audioItem = await createAudioItemFromObject(this, {
      uid,
      hashedFilename: audioItemData.filename,
      onPageStartTime,
      audioPlayer: {
        audioUrl: audioItemData.audioUrl ?? getAudioUrl(audioItemData.filename, audioItemData.src),
        originalDuration: audioItemData.duration,
        trim: {
          isTrimmed: false,
          startTime: 0,
          endTime: audioItemData.duration,
        },
      },
      source: audioItemData.src,
      name: audioItemData.name,
    });

    this.audioItemsHashMap[audioItem.uid] = audioItem;
    return audioItem.uid;
  }

  public async trimAudioItem(audioItemId: string, startTime: number, endTime: number, {undoable = true}: TrimAudioItemOpts = {}): Promise<void> {
    const audioItem = this.getAudioItemForId(audioItemId);
    if (audioItem) {
      await audioItem.updateFromObject(
        {
          audioPlayer: {
            trim: {
              isTrimmed: true,
              startTime,
              endTime,
            },
          },
        },
        {
          undoable,
          updateRedux: true,
        }
      );
    }
  }

  public getAudioItemForId(audioItemId: string): AudioItem | undefined {
    return this.audioItemsHashMap[audioItemId];
  }

  public async updateFromObject(
    audioClipsObject: DeepPartial<AudioClipsObject> = {},
    {updateRedux = true, fireModifiedEvent = true, undoable = true, recompactSubRows = true}: UpdateFromObjectOpts = {}
  ): Promise<void> {
    const {audioItemsHashMap, ...otherAudioPlaylistItemsObject} = audioClipsObject;
    this.copyVals({
      ...otherAudioPlaylistItemsObject,
    });
    // delete items that are in this class but not in the audioClipsObject
    if (audioItemsHashMap) {
      // Update/Add items from audioClipsObject
      const updateFromObjectPromises = [];

      const newAudioItemsHashmap: Record<string, AudioItem> = {};
      const addItemPromises = [];

      for (const [uid, audioItemObject] of Object.entries(audioItemsHashMap)) {
        if (audioItemObject) {
          if (this.audioItemsHashMap[uid]) {
            newAudioItemsHashmap[uid] = this.audioItemsHashMap[uid];
            updateFromObjectPromises.push(
              this.audioItemsHashMap[uid].updateFromObject(audioItemObject, {
                undoable: false,
                updateRedux: false,
                fireEvent: false,
              })
            );
          } else {
            addItemPromises.push(createAudioItemFromObject(this, audioItemObject));
          }
        }
      }

      const itemsToAdd = await Promise.all(addItemPromises);
      for (const itemToAdd of itemsToAdd) {
        newAudioItemsHashmap[itemToAdd.uid] = itemToAdd;
      }

      for (const [uid] of Object.entries(this.audioItemsHashMap)) {
        if (audioItemsHashMap[uid] === undefined) {
          this.audioItemsHashMap[uid].onRemove();
        }
      }

      this.audioItemsHashMap = newAudioItemsHashmap;
      this.#pruneSelectionToLiveClips();
      await Promise.all(updateFromObjectPromises);
    }

    if (recompactSubRows) {
      this.recalculateTimelineSubRows();
    }
    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.page.poster.fire('modified');
    }
    if (updateRedux) {
      this.page.poster.redux.updateReduxData();
    }
  }

  public getDuration(): number {
    return this.getMaxOnPageEndTime() - this.getMinOnPageStartTime();
  }

  public getMaxOnPageEndTime(): number {
    const lastAudioItem = this.getMaxOnPageEndTimeAudioItem();
    return lastAudioItem ? lastAudioItem.getOnPageEndTime() : 0;
  }

  private getMaxOnPageEndTimeAudioItem(): AudioItem | undefined {
    let maxOnPageEndTimeAudioItem: AudioItem | undefined = undefined;

    for (const [, audioItem] of Object.entries(this.audioItemsHashMap)) {
      maxOnPageEndTimeAudioItem ??= audioItem;

      if (audioItem.getOnPageEndTime() > maxOnPageEndTimeAudioItem.getOnPageEndTime()) {
        maxOnPageEndTimeAudioItem = audioItem;
      }
    }

    if (maxOnPageEndTimeAudioItem === undefined) {
      return undefined;
    }

    return maxOnPageEndTimeAudioItem;
  }

  private getMinOnPageStartTimeAudioItem(): AudioItem | undefined {
    let minOnPageStartTimeAudioItem: AudioItem | undefined = undefined;

    for (const [, audioItem] of Object.entries(this.audioItemsHashMap)) {
      minOnPageStartTimeAudioItem ??= audioItem;

      if (audioItem.onPageStartTime < minOnPageStartTimeAudioItem.onPageStartTime) {
        minOnPageStartTimeAudioItem = audioItem;
      }
    }

    if (minOnPageStartTimeAudioItem === undefined) {
      return undefined;
    }

    return minOnPageStartTimeAudioItem;
  }

  private getMinOnPageStartTime(): number {
    const firstAudioItem = this.getMinOnPageStartTimeAudioItem();
    return firstAudioItem ? firstAudioItem.onPageStartTime : 0;
  }

  public play(): void {
    for (const [, audioItem] of Object.entries(this.audioItemsHashMap)) {
      audioItem.play();
    }
  }

  public getTimeForAudioItem(audioItemId: string): number {
    return this.audioItemsHashMap[audioItemId].onPageStartTime + 0.01;
  }

  public pause(): void {
    for (const [, audioItem] of Object.entries(this.audioItemsHashMap)) {
      audioItem.audioPlayer.pause();
    }
  }

  public async seekToPageTime(pageTime: number): Promise<void> {
    const promises = [];
    for (const [, audioItem] of Object.entries(this.audioItemsHashMap)) {
      promises.push(audioItem.seekToPageTime(pageTime));
    }
    await Promise.all(promises);
  }

  public recalculateTimelineSubRows(prioritizedUids: readonly string[] = []): void {
    const entries = Object.values(this.audioItemsHashMap);
    recalculateTimelineSubRows(entries, (audio) => audio.getOnPageEndTime(), prioritizedUids);
  }

  private doRemoveItem(uid: string): void {
    this.audioItemsHashMap[uid].onRemove();
    const {[uid]: _, ...audioItemsHashMap} = this.audioItemsHashMap;
    this.audioItemsHashMap = audioItemsHashMap;
    void this.page.clampCurrentTime();
  }

  // Applies the final state of an audio-track drag: new start time and,
  // optionally, a new sub-row. When `insertNewRow` is set, clips at or below the
  // target row shift down first so the dragged clip lands on a fresh row.
  public moveAudioItemOnTimeline(uid: string, move: {startTime: number; newSubRow?: number; insertNewRow?: boolean}): void {
    const audioItem = this.audioItemsHashMap[uid];
    if (!audioItem) {
      return;
    }

    audioItem.onPageStartTime = Number(move.startTime.toFixed(2));

    if (move.newSubRow !== undefined) {
      if (move.insertNewRow) {
        for (const otherAudio of Object.values(this.audioItemsHashMap)) {
          if (otherAudio.uid !== uid && otherAudio.timelineSubRow >= move.newSubRow) {
            otherAudio.timelineSubRow += 1;
          }
        }
      }
      audioItem.timelineSubRow = move.newSubRow;
    }

    this.recalculateTimelineSubRows();
    commitPosterMutation(this.page);
  }

  // Applies the final state of an audio-track resize, converting the moved edge
  // into a source-trim change at the clip's playback speed.
  public async resizeAudioItemOnTimeline(uid: string, data: TimelineResizeData): Promise<void> {
    const audioItem = this.audioItemsHashMap[uid];
    if (!audioItem) {
      return;
    }
    await this.#applyTrimResize(audioItem, data);
    this.recalculateTimelineSubRows();
    commitPosterMutation(this.page);
  }

  /**
   * Applies a group drag to the audio members WITHOUT committing —
   * `Page.moveTimelineGroup` owns the clamps and the single history commit.
   */
  public applyGroupTimelineMove(uids: string[], deltaTime: number, rowDelta: number): void {
    for (const uid of uids) {
      const audioItem = this.audioItemsHashMap[uid];
      if (!audioItem) {
        continue;
      }
      audioItem.onPageStartTime = Number((audioItem.onPageStartTime + deltaTime).toFixed(2));
      audioItem.timelineSubRow = Math.max(0, audioItem.timelineSubRow + rowDelta);
    }
    // Members claim the rows the gesture placed them on; bystanders re-rack around them.
    this.recalculateTimelineSubRows(uids);
  }

  /**
   * Applies a group resize to the audio members WITHOUT committing — each clip's span
   * is clamped by the source it still has outside its trim, then applied through the
   * same trim conversion as a single-bar resize. `Page.resizeTimelineGroup` owns the
   * single history commit.
   */
  public async applyGroupTimelineResize(uids: string[], handle: TimelineResizeHandle, deltaTime: number): Promise<void> {
    for (const uid of uids) {
      const audioItem = this.audioItemsHashMap[uid];
      if (!audioItem) {
        continue;
      }
      const span = {startTime: audioItem.onPageStartTime, endTime: audioItem.getOnPageEndTime()};
      const resizedSpan = getGroupResizedSpan(span, audioItem.getTimelineResizeBounds(), handle, deltaTime);
      // Sequential on purpose: each trim change reinits the clip's audio buffer, and
      // group sizes are small — parallel reinits would race the shared Howler pool.
      // eslint-disable-next-line no-await-in-loop
      await this.#applyTrimResize(audioItem, {handle, startTime: resizedSpan.startTime, endTime: resizedSpan.endTime});
    }
    this.recalculateTimelineSubRows();
  }

  /**
   * Converts a resized on-page span into a source-trim change at the clip's playback
   * speed and applies it to the clip — shared by the single-bar resize and the group
   * resize so both commit identical trim math. Does not recalc rows or commit.
   */
  async #applyTrimResize(audioItem: AudioItem, data: TimelineResizeData): Promise<void> {
    const {speed, originalDuration, trim} = audioItem.audioPlayer;
    const originalStartTime = audioItem.onPageStartTime;
    const originalEndTime = audioItem.getOnPageEndTime();

    let newTrimStart = trim.startTime;
    let newTrimEnd = trim.endTime;

    if (data.startTime !== originalStartTime) {
      newTrimStart = Math.max(0, trim.startTime + (data.startTime - originalStartTime) * speed);
    }

    if (data.endTime !== originalEndTime) {
      newTrimEnd = Math.min(originalDuration, trim.endTime + (data.endTime - originalEndTime) * speed);
    }

    await audioItem.updateFromObject(
      {
        onPageStartTime: Number(Math.max(0, data.startTime).toFixed(2)),
        audioPlayer: {
          trim: {
            isTrimmed: newTrimStart !== 0 || newTrimEnd !== originalDuration,
            startTime: Number(newTrimStart.toFixed(2)),
            endTime: Number(newTrimEnd.toFixed(2)),
          },
        },
      },
      {updateRedux: false, undoable: false}
    );
  }

  public async splitAudioItem(audioItemId: string, splitTime: number): Promise<void> {
    if (await this.applySplit(audioItemId, splitTime)) {
      commitPosterMutation(this.page);
    }
  }

  /**
   * Splits a clip at `splitTime` into two clips WITHOUT committing — the caller owns
   * the history entry, so multi-bar splits land as one undo step. The original clip
   * keeps its uid as the left half. Returns false when the uid doesn't resolve.
   */
  public async applySplit(audioItemId: string, splitTime: number): Promise<boolean> {
    const audioItem = this.getAudioItemForId(audioItemId);
    if (!audioItem) {
      return false;
    }
    // Both halves must be at least MIN_ITEM_DURATION — a split time outside (or at the
    // edge of) the clip's span must never cut it, even when a caller passes a stale uid.
    if (splitTime - audioItem.onPageStartTime < MIN_ITEM_DURATION || audioItem.getOnPageEndTime() - splitTime < MIN_ITEM_DURATION) {
      return false;
    }

    const audioObject = audioItem.toObject();
    const {trim, speed} = audioObject.audioPlayer;
    const roundedSplitTime = Number(splitTime.toFixed(2));
    const timeIntoItem = roundedSplitTime - audioItem.onPageStartTime;
    const trimSplitPoint = Number((trim.startTime + timeIntoItem * speed).toFixed(2));

    await audioItem.updateFromObject(
      {
        audioPlayer: {
          trim: {
            isTrimmed: true,
            startTime: trim.startTime,
            endTime: trimSplitPoint,
          },
        },
      },
      {undoable: false}
    );

    // Anchor the new item to the first item's derived end so the two tile exactly;
    // using the raw split time would overlap the rounded trim boundary.
    const newAudioItem = await createAudioItemFromObject(this, {
      ...audioObject,
      uid: getUniqueString(),
      onPageStartTime: audioItem.getOnPageEndTime(),
      audioPlayer: {
        ...audioObject.audioPlayer,
        trim: {
          isTrimmed: true,
          startTime: trimSplitPoint,
          endTime: trim.endTime,
        },
      },
    });

    this.audioItemsHashMap[newAudioItem.uid] = newAudioItem;
    return true;
  }

  public duplicateAudio(audioUID: string): void {
    const audioItem = this.getAudioItemForId(audioUID);
    if (!audioItem) {
      return;
    }
    void this.page.items.addItems.addAudioItemFromAnotherAudioItem(audioItem);
  }
}
